import { AffiliatedAssociations, regex } from '@prishapolicy/backend-core/masters';
import { trpcNoAuth } from '@prishapolicy/shared/trpc';
import {
  Button,
  CheckToggle,
  DatePicker,
  DropDownSelect,
  Form,
  showToast,
  TextInput,
  useWindowWidth,
} from '@prishapolicy/shared/ui';
import { useInsuranceForDoctorsFormType } from '@prishapolicy/w3/ui';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { object } from 'yup';
import Link from 'next/link';

type BasicFormInfo = {
  firstName: string | null;
  lastName: string | null;
  gender: string | null;
  dateOfBirth: Date | null;
  mobileNumber: string | null;
  affilatedAssociation: string | null;
  emailId: string | null;
};

export const BasicFlowForm = () => {
  const { setFormInfo, setMoreOffersForm, formInfo } = useInsuranceForDoctorsFormType();
  const { width } = useWindowWidth();
  const { isLg } = useWindowWidth(0);

  const twentyYearsAgo = dayjs().subtract(20, 'year').toDate();

  const createLeadMutation = trpcNoAuth['lead-management']['lead']['create-lead-for-doctors'].useMutation();

  let storedFormInfo: BasicFormInfo = {
    firstName: null,
    lastName: null,
    gender: null,
    dateOfBirth: null,
    mobileNumber: null,
    affilatedAssociation: null,
    emailId: null,
  };

  if (typeof window !== 'undefined') {
    const savedInfo = localStorage.getItem('basicFormInfo');
    if (savedInfo) {
      storedFormInfo = JSON.parse(savedInfo);
    }
  }

  return (
    <Form
      initialValues={{
        firstName: storedFormInfo.firstName || formInfo.firstName || null,
        lastName: storedFormInfo.lastName || formInfo.lastName || null,
        gender: storedFormInfo.gender || formInfo.gender || null,
        dateOfBirth: storedFormInfo.dateOfBirth || formInfo.dateOfBirth || null,
        mobileNumber: storedFormInfo.mobileNumber || formInfo.mobileNumber || null,
        affilatedAssociation: storedFormInfo.affilatedAssociation || formInfo.affilatedAssociation || null,
        emailId: storedFormInfo.emailId || formInfo.emailId || null,
        termsAndCondition: true,
        contactWhatsapp: true,
      }}
      validationSchema={object().shape({
        firstName: Yup.string().required('First Name is Required').typeError('First Name is Required'),
        lastName: Yup.string().required('Last Name is Required').typeError('Last Name is Required'),
        gender: Yup.string().required('Gender is required').typeError('Gender is Required'),
        dateOfBirth: Yup.date().required('Date Of Birth is Required').typeError('Date Of Birth is Required'),
        mobileNumber: Yup.string()
          .matches(regex.phone, 'Invalid Phone Number')
          .required('Mobile Number is Required')
          .typeError('Mobile Number is Required'),
        affilatedAssociation: Yup.string()
          .required('Affilated Association is required')
          .typeError('Affilated Association is required'),
        emailId: Yup.string().matches(regex.email, 'Invalid Email Address').nullable(), //remove required+type error
        termsAndCondition: Yup.boolean(),
        contactWhatsapp: Yup.boolean(),
      })}
      onSubmit={async (values) => {
        console.log('values', values);

        localStorage.setItem('basicFormInfo', JSON.stringify(values));

        setFormInfo({
          affilatedAssociation: values.affilatedAssociation,
          dateOfBirth: values.dateOfBirth,
          emailId: values.emailId,
          firstName: values.firstName,
          gender: values.gender,
          lastName: values.lastName,
          mobileNumber: values.mobileNumber,
        });

        const data = await createLeadMutation.mutateAsync({
          firstName: values.firstName,
          lastName: values.lastName,
          gender: values.gender,
          dateOfBirth: values.dateOfBirth,
          mobileNumber: values.mobileNumber,
          affilatedAssociation: values.affilatedAssociation,
          emailId: values.emailId,
        });

        const leadId = data?.id;

        setFormInfo({
          leadId: leadId,
        });

        // setCoverageDetailsForm();
        setMoreOffersForm();

        showToast({
          title: 'Thank you for showing your interest',
          description:
            'Your request has been submitted to a certified PrishaPolicy executive who will be reaching out to you soon.',
          variant: 'success',
        });
      }}
      className=" grid rounded-lg bg-white px-5 pb-5"
    >
      <div className="scrollbar-hide grid max-h-[600px]  gap-3 overflow-auto whitespace-nowrap ">
        <div className="text-title-6 font-bold">Let’s start with your basic Info</div>
        <div className={`${isLg ? 'flex items-center justify-center gap-4' : ''}`}>
          <TextInput name="firstName" placeholder="Enter Your First Name" label="First Name" />
          <TextInput name="lastName" placeholder="Enter Your Last Name" label="Last Name" />
        </div>

        <div className={`${isLg ? 'flex items-center justify-between gap-4' : ''}`}>
          <div className="flex-1">
            <DropDownSelect
              label="Gender"
              showSearchBar={false}
              options={[
                {
                  options: ['Male', 'Female'].map((e) => ({
                    value: e,
                    mainText: e,
                  })),
                },
              ]}
              name="gender"
            />
          </div>
          <div className="flex-1">
            <DatePicker
              name="dateOfBirth"
              label="Date of Birth"
              minDate={new Date(1900, 0, 1)}
              maxDate={twentyYearsAgo}
              disableFuture={true}
            />
          </div>
        </div>
        <TextInput name="mobileNumber" placeholder="Enter Your Mobile No." label="Phone No." />

        <DropDownSelect
          name="affilatedAssociation"
          placeholder="Enter Affiliated Association"
          label="Affiliated Association"
          showSearchBar={false}
          options={[
            {
              options: AffiliatedAssociations.map((e) => ({
                value: e,
                mainText: e,
              })),
            },
          ]}
        />
        <TextInput name="emailId" placeholder="Enter your email id" label="Email Id" />

        <CheckToggle
          name="termsAndCondition"
          label={
            <span className="text-grey-500">
              I agree to the
              <Link href="/about/terms-and-conditions">
                <span className="text-primary ml-1 underline hover:cursor-pointer">terms and conditions</span>
              </Link>
            </span>
          }
        />

        <div>
          <CheckToggle name="contactWhatsapp" label={<span className="text-grey-500">Get Update On WhatsApp</span>} />
          {width > 480 ? (
            <p className="text-grey-500 ml-7 mt-1">
              Only<strong> certified PrishaPolicy executives</strong> will assist you.
            </p>
          ) : (
            <p className="text-grey-500 ml-7 mt-1">
              Only<strong> certified PrishaPolicy</strong> <br /> <strong>executives</strong> will assist you.
            </p>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 mt-3 rounded-lg bg-white">
        <Button
          text="Get Quote"
          rightIcon="arrowRight"
          type="submit"
          className="w-full"
          disabled={createLeadMutation.isPending}
        />
      </div>
    </Form>
  );
};
